import React, { useState, useEffect, useCallback } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import Spinner from './spinner/Spinner';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import useGetUserRole from '../hooks/useGetUserRole';
import { isAdminOrAbove } from '../utils/roles';
import { publicEnv } from '../publicEnv';

const schema = yup.object({
  orientation_zoom_link: yup
    .string()
    .url('Must be a valid URL')
    .required('Zoom link is required'),
  orientation_meeting_id: yup.string(),
  orientation_passcode: yup.string(),
  schedule_pdf_url: yup.string().url('Must be a valid URL').nullable(),
  learner_guide_url: yup.string().url('Must be a valid URL').nullable(),
  what_to_expect_url: yup.string().url('Must be a valid URL').nullable(),
  is_automation_enabled: yup.boolean(),
});

// makeSessionRowId returns a stable client-side id for a session row so
// React keys stay correct when rows are added or removed.
function makeSessionRowId() {
  return `s_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
}

// Convert a "datetime-local" input value (YYYY-MM-DDTHH:mm in local time)
// to an RFC3339 timestamp the backend accepts.
function localDateTimeToISO(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

// Convert an RFC3339 timestamp coming back from the API to the YYYY-MM-DDTHH:mm
// format the datetime-local input expects, in the user's local timezone.
function isoToLocalDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const localMs = d.getTime() - d.getTimezoneOffset() * 60000;
  return new Date(localMs).toISOString().slice(0, 16);
}

// Status badge component
function StatusBadge({ status }) {
  const statusConfig = {
    PENDING: {
      color: 'bg-silver-300 text-dove-600',
      icon: null,
      label: 'Pending',
    },
    SCHEDULED: {
      color: 'bg-persian-100 text-persian-700',
      icon: <ScheduleIcon fontSize="inherit" />,
      label: 'Scheduled',
    },
    SENT: {
      color: 'bg-lms-custom-200 text-green-800',
      icon: <CheckCircleIcon fontSize="inherit" />,
      label: 'Sent',
    },
    FAILED: {
      color: 'bg-claret-50 text-claret-600',
      icon: <ErrorOutlineIcon fontSize="inherit" />,
      label: 'Failed',
    },
  };
  const config = statusConfig[status] || statusConfig.PENDING;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

// Email Template Editor Component
function EmailTemplateEditor({
  label,
  description,
  value,
  onChange,
  onPreview,
  onReset,
}) {
  const [isEditing, setIsEditing] = useState(false);

  const { quill, quillRef } = useQuill({
    modules: {
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['link'],
        [{ color: [] }, { background: [] }],
        ['clean'],
      ],
      clipboard: {
        matchVisual: false,
      },
    },
    placeholder: 'Compose your email template...',
  });

  const handleChange = useCallback(
    (content) => {
      onChange(content);
    },
    [onChange]
  );

  useEffect(() => {
    if (quill && isEditing) {
      quill.clipboard.dangerouslyPasteHTML(value || '');
    }
  }, [quill, isEditing, value]);

  useEffect(() => {
    if (quill) {
      quill.on('text-change', () => {
        handleChange(quill.root.innerHTML);
      });
    }
  }, [quill, handleChange]);

  return (
    <div className="border border-silver-400 rounded-lg p-4">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-base font-semibold text-mine-shaft-600">
            {label}
          </h3>
          <p className="text-xs text-dove-500">{description}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onReset}
            className="text-dove-500 hover:text-mine-shaft-600 text-sm flex items-center gap-1"
            title="Reset to default template"
          >
            <RestoreIcon fontSize="small" />
            Reset
          </button>
          <button
            type="button"
            onClick={() => onPreview(value)}
            className="text-persian-600 hover:text-persian-700 text-sm flex items-center gap-1"
          >
            <VisibilityIcon fontSize="small" />
            Preview
          </button>
          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            className="text-claret-600 hover:text-claret-700 text-sm flex items-center gap-1"
          >
            <EditIcon fontSize="small" />
            {isEditing ? 'Close Editor' : 'Edit Template'}
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="mt-3">
          <div className="bg-lms-custom-50 rounded p-2 mb-2 text-xs text-dove-600">
            <strong>Available Variables:</strong>{' '}
            {`{{firstname}}, {{cohort_name}}, {{orientation_date}}, {{orientation_time}}, {{zoom_link}}, {{meeting_id}}, {{passcode}}`}
          </div>
          <div ref={quillRef} style={{ minHeight: '300px' }} />
        </div>
      )}

      {!isEditing && value && (
        <div className="mt-2 p-3 bg-silver-100 rounded border text-xs text-dove-500 max-h-20 overflow-hidden">
          Email template configured ({value.length} characters)
        </div>
      )}
    </div>
  );
}

// Email Preview Modal
function EmailPreviewModal({ html, onClose }) {
  if (!html) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Email Preview</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-dove-500 hover:text-mine-shaft-600"
          >
            <CancelIcon />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </div>
        <div className="p-4 border-t bg-silver-50 text-center">
          <p className="text-xs text-dove-500">
            This is a preview. Variables like {`{{firstname}}`} will be replaced
            with actual values when sent.
          </p>
        </div>
      </div>
    </div>
  );
}

function CohortOnboardingConfig({ cohortId, toggleModal }) {
  const role = useGetUserRole();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTriggeringAutomation, setIsTriggeringAutomation] = useState(false);
  const [configExists, setConfigExists] = useState(false);
  const [defaultEmail2Template, setDefaultEmail2Template] = useState('');
  const [defaultEmail3Template, setDefaultEmail3Template] = useState('');
  const [email2Template, setEmail2Template] = useState('');
  const [email3Template, setEmail3Template] = useState('');
  const [previewHtml, setPreviewHtml] = useState(null);
  const [sessions, setSessions] = useState(() => [
    { rowId: makeSessionRowId(), session_number: 1, session_date_local: '' },
  ]);
  const [sessionsError, setSessionsError] = useState('');

  // No local fallbacks: default templates must come from the gateway

  // Fetch default templates on component mount
  useEffect(() => {
    const fetchDefaultTemplates = async () => {
      const apiBase =
        publicEnv('REACT_APP_API_BASE_URL') ||
        publicEnv('REACT_APP_BASE_URL_v2') ||
        '';

      try {
        const response = await fetch(
          `${apiBase}/cohorts/onboarding-templates/default`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        if (response.ok) {
          const contentType = (
            response.headers.get('content-type') || ''
          ).toLowerCase();
          if (contentType.includes('application/json')) {
            const data = await response.json();
            setDefaultEmail2Template(data.orientation_access || '');
            setDefaultEmail3Template(data.orientation_reminder || '');
            // Initialize template states with defaults
            setEmail2Template(data.orientation_access || '');
            setEmail3Template(data.orientation_reminder || '');
          } else {
            const text = await response.text();
            console.error(
              `Default templates endpoint returned non-JSON (status ${response.status}):`,
              text.substring ? text.substring(0, 200) : text
            );
            throw new Error('Non-JSON response from templates endpoint');
          }
        }
      } catch (error) {
        console.error('Failed to fetch default templates:', error);
        // No fallback — require valid authenticated access to the gateway to load templates
        setDefaultEmail2Template('');
        setDefaultEmail3Template('');
        setEmail2Template('');
        setEmail3Template('');
      }
    };

    fetchDefaultTemplates();
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      is_automation_enabled: false,
    },
  });

  const queryClient = useQueryClient();

  // Fetch existing config
  const {
    data: existingConfig,
    isLoading: isLoadingConfig,
    refetch,
  } = useFetchDatav2(
    ['cohort-onboarding-config', cohortId],
    `/cohorts/${cohortId}/onboarding-config`,
    {},
    null,
    !!cohortId
  );

  useEffect(() => {
    if (existingConfig?.data?.config) {
      const { config } = existingConfig.data;
      setConfigExists(true);

      reset({
        orientation_zoom_link: config.orientation_zoom_link || '',
        orientation_meeting_id: config.orientation_meeting_id || '',
        orientation_passcode: config.orientation_passcode || '',
        schedule_pdf_url: config.schedule_pdf_url || '',
        learner_guide_url: config.learner_guide_url || '',
        what_to_expect_url: config.what_to_expect_url || '',
        is_automation_enabled: config.is_automation_enabled || false,
      });

      // Hydrate sessions: prefer the new orientation_sessions array; fall back
      // to the legacy single orientation_date for any pre-migration cohort.
      const incomingSessions = Array.isArray(config.orientation_sessions)
        ? config.orientation_sessions
        : [];
      if (incomingSessions.length > 0) {
        setSessions(
          incomingSessions
            .slice()
            .sort((a, b) => (a.session_number || 0) - (b.session_number || 0))
            .map((s, idx) => ({
              rowId: makeSessionRowId(),
              session_number: idx + 1,
              session_date_local: isoToLocalDateTime(s.session_date),
            }))
        );
      } else if (config.orientation_date) {
        setSessions([
          {
            rowId: makeSessionRowId(),
            session_number: 1,
            session_date_local: isoToLocalDateTime(config.orientation_date),
          },
        ]);
      } else {
        setSessions([
          {
            rowId: makeSessionRowId(),
            session_number: 1,
            session_date_local: '',
          },
        ]);
      }

      if (config.email2_template) {
        setEmail2Template(config.email2_template);
      }
      if (config.email3_template) {
        setEmail3Template(config.email3_template);
      }
    }
  }, [existingConfig, reset]);

  const handleSessionChange = (index, value) => {
    setSessions((prev) =>
      prev.map((s, i) =>
        i === index ? { ...s, session_date_local: value } : s
      )
    );
    setSessionsError('');
  };

  const handleAddSession = () => {
    setSessions((prev) => {
      // Pre-fill the new row's date from the first session so the admin only
      // has to pick the time. Orientation runs on one day across all sessions;
      // this enforces that contract by default. Use 09:00 as the placeholder
      // hour — admins always change it before submitting.
      const firstFull =
        prev.length > 0 && prev[0].session_date_local
          ? `${prev[0].session_date_local.slice(0, 10)}T09:00`
          : '';
      return [
        ...prev,
        {
          rowId: makeSessionRowId(),
          session_number: prev.length + 1,
          session_date_local: firstFull,
        },
      ];
    });
  };

  const handleRemoveSession = (index) => {
    setSessions((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, session_number: i + 1 }))
    );
  };

  // Create mutation
  const createMutation = useMutation(
    (data) => axiosInstancev2.post(`/cohorts/onboarding-config`, data),
    {
      onSuccess: () => {
        toast.success('Onboarding configuration saved successfully!');
        queryClient.invalidateQueries(['cohort-onboarding-config', cohortId]);
        setIsSubmitting(false);
        setConfigExists(true);
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.error || 'Failed to save configuration'
        );
        setIsSubmitting(false);
      },
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    (data) =>
      axiosInstancev2.put(`/cohorts/${cohortId}/onboarding-config`, data),
    {
      onSuccess: () => {
        toast.success('Configuration updated successfully!');
        queryClient.invalidateQueries(['cohort-onboarding-config', cohortId]);
        setIsSubmitting(false);
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.error || 'Failed to update configuration'
        );
        setIsSubmitting(false);
      },
    }
  );

  // Trigger automation mutation
  const triggerMutation = useMutation(
    () =>
      axiosInstancev2.post(`/cohorts/${cohortId}/onboarding-config/trigger`),
    {
      onSuccess: (response) => {
        toast.success(
          response?.data?.message || 'Onboarding automation triggered!'
        );
        queryClient.invalidateQueries(['cohort-onboarding-config', cohortId]);
        if (typeof refetch === 'function') {
          refetch();
        }
        setIsTriggeringAutomation(false);
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.error || 'Failed to trigger automation'
        );
        setIsTriggeringAutomation(false);
      },
    }
  );

  const onSubmit = async (data) => {
    const filledSessions = sessions.filter((s) => s.session_date_local);
    if (filledSessions.length === 0) {
      setSessionsError('At least one orientation session is required');
      return;
    }
    const isoDates = filledSessions.map((s) =>
      localDateTimeToISO(s.session_date_local)
    );
    if (new Set(isoDates).size !== isoDates.length) {
      setSessionsError('Two sessions cannot have the same date and time');
      return;
    }
    // Orientation runs on a single calendar day with multiple time slots.
    // Anchoring on the YYYY-MM-DD prefix of the local input value keeps this
    // check timezone-safe (no Date object drift across DST/TZ boundaries).
    const localDates = filledSessions.map((s) =>
      (s.session_date_local || '').slice(0, 10)
    );
    if (new Set(localDates).size > 1) {
      setSessionsError(
        'All sessions must be on the same date — only the times can differ'
      );
      return;
    }
    setSessionsError('');

    setIsSubmitting(true);
    const requestData = {
      ...data,
      cohort_id: cohortId,
      // Legacy field — server uses orientation_sessions[0] when both are present,
      // but we keep this for any older gateway version still in flight.
      orientation_date: isoDates[0],
      orientation_sessions: filledSessions.map((s, idx) => ({
        session_number: idx + 1,
        session_date: isoDates[idx],
      })),
      email2_template: email2Template,
      email3_template: email3Template,
    };

    if (configExists) {
      updateMutation.mutate(requestData);
    } else {
      createMutation.mutate(requestData);
    }
  };

  const handleTriggerAutomation = () => {
    if (
      !window.confirm(
        'Are you sure you want to trigger the onboarding automation? This will send emails to all students in this cohort.'
      )
    ) {
      return;
    }
    setIsTriggeringAutomation(true);
    triggerMutation.mutate();
  };

  // Get current status from config
  const configData = existingConfig?.data?.config || existingConfig?.data || {};
  const email2Status =
    configData.email2_status || configData.email2Status || 'PENDING';
  const email3Status =
    configData.email3_status || configData.email3Status || 'PENDING';
  const peerGroupStatus =
    configData.peer_group_status || configData.peerGroupStatus || 'PENDING';

  if (!isAdminOrAbove(role)) {
    return (
      <div className="text-center py-8">
        <p className="text-claret-500 font-medium">
          Access Denied: This feature is only available to Admins and
          SuperAdmins.
        </p>
      </div>
    );
  }

  if (isLoadingConfig) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  const sessionInputClass = `border border-silver-500 px-3 py-2 rounded-md focus:outline-claret-500 ${
    sessionsError ? 'border-claret-500' : ''
  }`;

  const zoomInputClass = `border border-silver-500 px-3 py-2 rounded-md focus:outline-claret-500 ${
    errors.orientation_zoom_link ? 'border-claret-500' : ''
  }`;

  const triggerButtonClass = `bg-persian-500 px-4 py-2 rounded-md text-white text-sm flex items-center gap-1 hover:bg-persian-600 disabled:opacity-50 ${
    !configExists ? 'cursor-not-allowed opacity-50' : ''
  }`;

  return (
    <div className="max-h-[80vh] overflow-y-auto pr-2">
      {/* Preview Modal */}
      {previewHtml && (
        <EmailPreviewModal
          html={previewHtml}
          onClose={() => setPreviewHtml(null)}
        />
      )}

      {/* SOP Info Banner */}
      <div className="mb-4 p-3 bg-lms-custom-50 rounded-lg border border-silver-400">
        <div className="flex items-start gap-2 text-mine-shaft-600">
          <InfoOutlinedIcon fontSize="small" className="mt-0.5" />
          <div className="text-sm">
            <p className="font-medium mb-1">
              Automated Email Schedule (max 3 emails):
            </p>
            <ul className="text-xs text-dove-500 space-y-0.5">
              <li>
                <strong>Email 1:</strong> Selection Confirmation with commitment
                form{' '}
                <span className="text-persian-600">
                  (auto-sent on enrollment)
                </span>
              </li>
              <li>
                <strong>Email 2:</strong> Orientation Access & Program Overview{' '}
                <span className="text-persian-600">
                  (sent immediately when triggered)
                </span>
              </li>
              <li>
                <strong>Email 3:</strong> Orientation Reminder & Checklist{' '}
                <span className="text-persian-600">
                  (exactly 1 day before orientation)
                </span>
              </li>
              <li>
                <strong>Deskies:</strong> Peer group allocation{' '}
                <span className="text-persian-600">
                  (1 week after orientation)
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Automation Status */}
      <div className="mb-4 p-3 border border-silver-400 rounded-lg">
        <h3 className="text-sm font-semibold mb-2 text-mine-shaft-600">
          Automation Status
        </h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-dove-500">Email 2:</span>
            <StatusBadge status={email2Status} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-dove-500">Email 3:</span>
            <StatusBadge status={email3Status} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-dove-500">Deskies:</span>
            <StatusBadge status={peerGroupStatus} />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Orientation Session Details */}
        <div className="border border-silver-400 rounded-lg p-4">
          <h3 className="text-base font-semibold mb-1 text-mine-shaft-600">
            Orientation Session Details
          </h3>
          <p className="text-xs text-dove-500 mb-3">
            Orientation runs on a single day with one or more time slots. All
            sessions must share the same date; only the time can differ. They
            also share the Zoom link, meeting ID and passcode below. Learners
            receive one email listing every session and pick the one they can
            attend.
          </p>

          <div className="space-y-2 mb-4">
            <label className="text-sm pl-1">
              Orientation Sessions <span className="text-claret-500">*</span>
            </label>
            {sessions.map((session, index) => (
              <div key={session.rowId} className="flex items-center gap-2">
                <span className="text-sm text-dove-600 w-20 shrink-0">
                  Session {index + 1}
                </span>
                <input
                  type="datetime-local"
                  value={session.session_date_local}
                  onChange={(e) => handleSessionChange(index, e.target.value)}
                  className={`${sessionInputClass} flex-1`}
                />
                {sessions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSession(index)}
                    className="text-claret-500 hover:text-claret-600 p-1"
                    title={`Remove session ${index + 1}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddSession}
              className="text-persian-600 hover:text-persian-700 text-sm flex items-center gap-1 mt-1"
            >
              <AddIcon fontSize="small" />
              Add another session
            </button>
            {sessionsError && (
              <span className="text-claret-500 text-xs block">
                {sessionsError}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col space-y-1 col-span-2">
              <label htmlFor="orientation_zoom_link" className="text-sm pl-1">
                Zoom Webinar Link <span className="text-claret-500">*</span>
              </label>
              <input
                {...register('orientation_zoom_link')}
                type="url"
                id="orientation_zoom_link"
                placeholder="https://zoom.us/j/..."
                className={zoomInputClass}
              />
              <span className="text-xs text-dove-500 pl-1">
                Used for every session above.
              </span>
            </div>

            <div className="flex flex-col space-y-1">
              <label htmlFor="orientation_meeting_id" className="text-sm pl-1">
                Meeting ID
              </label>
              <input
                {...register('orientation_meeting_id')}
                type="text"
                id="orientation_meeting_id"
                placeholder="123 456 7890"
                className="border border-silver-500 px-3 py-2 rounded-md focus:outline-claret-500"
              />
            </div>

            <div className="flex flex-col space-y-1">
              <label htmlFor="orientation_passcode" className="text-sm pl-1">
                Passcode
              </label>
              <input
                {...register('orientation_passcode')}
                type="text"
                id="orientation_passcode"
                placeholder="passcode123"
                className="border border-silver-500 px-3 py-2 rounded-md focus:outline-claret-500"
              />
            </div>
          </div>
        </div>

        {/* Email 2 Resources */}
        <div className="border border-silver-400 rounded-lg p-4">
          <h3 className="text-base font-semibold mb-1 text-mine-shaft-600">
            Email 2: Resources & Links
          </h3>
          <p className="text-xs text-dove-500 mb-3">
            Add links to resources that will be included in Email 2.
          </p>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex flex-col space-y-1">
              <label htmlFor="schedule_pdf_url" className="text-sm pl-1">
                Program Schedule PDF
              </label>
              <input
                {...register('schedule_pdf_url')}
                type="url"
                id="schedule_pdf_url"
                placeholder="https://drive.google.com/..."
                className="border border-silver-500 px-3 py-2 rounded-md focus:outline-claret-500"
              />
            </div>
            <div className="flex flex-col space-y-1">
              <label htmlFor="what_to_expect_url" className="text-sm pl-1">
                What to Expect Document
              </label>
              <input
                {...register('what_to_expect_url')}
                type="url"
                id="what_to_expect_url"
                placeholder="https://drive.google.com/..."
                className="border border-silver-500 px-3 py-2 rounded-md focus:outline-claret-500"
              />
            </div>
            <div className="flex flex-col space-y-1">
              <label htmlFor="learner_guide_url" className="text-sm pl-1">
                Learner Guide
              </label>
              <input
                {...register('learner_guide_url')}
                type="url"
                id="learner_guide_url"
                placeholder="https://drive.google.com/..."
                className="border border-silver-500 px-3 py-2 rounded-md focus:outline-claret-500"
              />
            </div>
          </div>
        </div>

        {/* Email 2 Template Editor */}
        <EmailTemplateEditor
          label="Email 2: Orientation Access Template"
          description="Sent immediately when automation is triggered. Contains orientation details and resources."
          value={email2Template}
          onChange={setEmail2Template}
          onPreview={(value) => setPreviewHtml(value || defaultEmail2Template)}
          onReset={() => setEmail2Template(defaultEmail2Template)}
        />

        {/* Email 3 Template Editor */}
        <EmailTemplateEditor
          label="Email 3: Orientation Reminder Template"
          description="Sent exactly 1 day before orientation. Final reminder with checklist."
          value={email3Template}
          onChange={setEmail3Template}
          onPreview={(value) => setPreviewHtml(value || defaultEmail3Template)}
          onReset={() => setEmail3Template(defaultEmail3Template)}
        />

        {/* Peer Groups Section */}
        <div className="border border-silver-400 rounded-lg p-4">
          <h3 className="text-base font-semibold mb-1 text-mine-shaft-600">
            Peer Groups & Desks (auto-generated)
          </h3>
          <p className="text-xs text-dove-500">
            Peer groups and desk allocations are generated automatically 3 days
            after the orientation session and published 7 days after. No manual
            spreadsheet setup is needed - the matching engine pairs learners by
            MBTI and emails everyone their peer group on schedule.
          </p>
        </div>

        {/* Automation Toggle */}
        <div className="border border-silver-400 rounded-lg p-4 bg-lms-custom-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-mine-shaft-600">
                Enable Automation
              </h3>
              <p className="text-sm text-dove-500">
                Emails will be scheduled based on the orientation date.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                {...register('is_automation_enabled')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-silver-400 peer-focus:ring-4 peer-focus:ring-claret-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-silver-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-claret-500" />
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        {isSubmitting ? (
          <div className="w-full flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <div className="flex justify-between items-center pt-4 border-t border-silver-400">
            <button
              type="button"
              className="border border-claret-500 px-4 py-2 rounded-md text-claret-500 text-sm flex items-center gap-1 hover:bg-claret-50"
              onClick={toggleModal}
            >
              <CancelIcon fontSize="inherit" />
              <span>Cancel</span>
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                className={triggerButtonClass}
                onClick={handleTriggerAutomation}
                disabled={isTriggeringAutomation || !configExists}
                title={
                  !configExists
                    ? 'Save configuration first to enable triggering'
                    : 'Trigger automation manually'
                }
              >
                {isTriggeringAutomation ? (
                  <Spinner size="small" />
                ) : (
                  <>
                    <PlayArrowIcon fontSize="inherit" />
                    <span>Trigger Automation</span>
                  </>
                )}
              </button>

              <button
                type="submit"
                className="bg-claret-500 px-4 py-2 rounded-md text-white text-sm flex items-center gap-1 hover:bg-claret-600"
              >
                <SaveIcon fontSize="inherit" />
                <span>{configExists ? 'Update Config' : 'Save Config'}</span>
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

export default CohortOnboardingConfig;
